import type { LessonLog, LessonOverride, MakeupSchedule } from '@/types';
import { formatDate } from '@/lib/datetime';

interface ParentTimelineSectionProps {
  overrides: LessonOverride[];
  schedules: MakeupSchedule[];
  lessons: LessonLog[];
}

type Tone = 'primary' | 'accent' | 'secondary' | 'muted';

interface TimelineItem {
  id: string;
  date: string;
  kind: 'time' | 'move' | 'cancel' | 'makeup';
  label: string;
  icon: string;
  tone: Tone;
  /** 상세 줄에서 사용할 값들 */
  startTime?: string;
  endTime?: string;
  movedToDate?: string | null;
  note?: string | null;
  /** 보충 전용 */
  reason?: string;
  done?: boolean;
  scope?: string | null;
  nextHomework?: string | null;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAYS[d.getDay()];
}

function toneClass(tone: Tone): string {
  if (tone === 'primary') return 'bg-primary-100 text-primary-700';
  if (tone === 'accent') return 'bg-accent-100 text-accent-700';
  if (tone === 'secondary') return 'bg-secondary-100 text-secondary-900';
  return 'bg-background-200 text-foreground-600';
}

function dotClass(tone: Tone): string {
  if (tone === 'primary') return 'bg-primary-500';
  if (tone === 'accent') return 'bg-accent-500';
  if (tone === 'secondary') return 'bg-secondary-500';
  return 'bg-foreground-400';
}

const LEGEND: { label: string; icon: string; tone: Tone }[] = [
  { label: '수업시간 변경', icon: 'ri-equalizer-line', tone: 'accent' },
  { label: '날짜 변경', icon: 'ri-calendar-2-line', tone: 'primary' },
  { label: '휴강', icon: 'ri-user-unfollow-line', tone: 'muted' },
  { label: '보충 수업', icon: 'ri-calendar-schedule-line', tone: 'secondary' },
];

export default function ParentTimelineSection({
  overrides,
  schedules,
  lessons,
}: ParentTimelineSectionProps) {
  const lessonMap: Record<string, LessonLog> = {};
  lessons.forEach((l) => {
    lessonMap[l.id] = l;
  });

  const items: TimelineItem[] = [
    ...overrides.map<TimelineItem>((o) => {
      if (o.kind === 'move') {
        return {
          id: `o-${o.id}`,
          date: o.override_date,
          kind: 'move',
          label: '날짜 변경',
          icon: 'ri-calendar-2-line',
          tone: 'primary',
          startTime: o.start_time,
          endTime: o.end_time,
          movedToDate: o.moved_to_date,
          note: o.note,
        };
      }
      if (o.kind === 'cancel') {
        return {
          id: `o-${o.id}`,
          date: o.override_date,
          kind: 'cancel',
          label: '휴강',
          icon: 'ri-user-unfollow-line',
          tone: 'muted',
          note: o.note,
        };
      }
      return {
        id: `o-${o.id}`,
        date: o.override_date,
        kind: 'time',
        label: '수업시간 변경',
        icon: 'ri-equalizer-line',
        tone: 'accent',
        startTime: o.start_time,
        endTime: o.end_time,
        note: o.note,
      };
    }),
    ...schedules.map<TimelineItem>((s) => {
      const linked = s.lesson_log_id ? lessonMap[s.lesson_log_id] : null;
      return {
        id: `m-${s.id}`,
        date: s.scheduled_date,
        kind: 'makeup',
        label: '보충 수업',
        icon: 'ri-calendar-schedule-line',
        tone: 'secondary',
        reason: s.reason || '보충',
        note: s.note,
        done: Boolean(linked),
        scope: linked?.lesson_scope ?? null,
        nextHomework: linked?.next_homework ?? null,
      };
    }),
  ].sort((a, b) => {
    if (a.date === b.date) return a.id < b.id ? -1 : 1;
    return a.date < b.date ? -1 : 1;
  });

  const hasTime = (item: TimelineItem) => Boolean(item.startTime || item.endTime);

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-foreground-100 text-foreground-700">
          <i className="ri-timeline-view"></i>
        </span>
        일정 변경 · 보충 타임라인
        <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-600">
          {items.length}건
        </span>
      </h2>
      <p className="mt-1.5 text-xs text-foreground-500">
        수업 일정 변경과 보충 수업을 날짜순으로 한눈에 정리했어요.
      </p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {LEGEND.map((l) => (
          <span
            key={l.label}
            className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${toneClass(
              l.tone,
            )}`}
          >
            <i className={l.icon}></i>
            {l.label}
          </span>
        ))}
      </div>

      {items.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-background-300 py-12 text-center text-sm text-foreground-500">
          아직 일정 변경이나 보충 수업이 없어요.
        </p>
      ) : (
        <ol className="mt-5 flex flex-col">
          {items.map((item, idx) => {
            const last = idx === items.length - 1;
            return (
              <li key={item.id} className="flex gap-3 md:gap-4">
                <div className="flex w-4 shrink-0 flex-col items-center">
                  <span className={`mt-1.5 h-3 w-3 rounded-full ring-4 ring-background-50 ${dotClass(item.tone)}`} />
                  {!last && <span className="mt-1 w-px flex-1 bg-background-200" />}
                </div>

                <div className={`min-w-0 flex-1 ${last ? 'pb-0' : 'pb-5'}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                      <i className="ri-calendar-line"></i>
                      {formatDate(item.date)}
                      {weekdayLabel(item.date) && ` (${weekdayLabel(item.date)})`}
                    </span>
                    <span
                      className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${toneClass(
                        item.tone,
                      )}`}
                    >
                      <i className={item.icon}></i>
                      {item.label}
                    </span>
                    {item.kind === 'makeup' && (
                      <span
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          item.done
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        <i className={item.done ? 'ri-check-line' : 'ri-time-line'}></i>
                        {item.done ? '수업 완료' : '보충 예정'}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 rounded-lg border border-background-200 bg-background-50 px-3.5 py-3">
                    {item.kind === 'move' && (
                      <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-foreground-900">
                        <span>{formatDate(item.date)}</span>
                        <i className="ri-arrow-right-line text-primary-600"></i>
                        <span className="text-primary-700">
                          {item.movedToDate ? formatDate(item.movedToDate) : '-'}
                          {item.movedToDate &&
                            weekdayLabel(item.movedToDate) &&
                            ` (${weekdayLabel(item.movedToDate)})`}
                        </span>
                        {hasTime(item) && (
                          <span className="flex items-center gap-1 text-xs font-medium text-foreground-600">
                            <i className="ri-time-line"></i>
                            {item.startTime}
                            {item.endTime ? ` ~ ${item.endTime}` : ''}
                          </span>
                        )}
                      </p>
                    )}

                    {item.kind === 'time' && hasTime(item) && (
                      <p className="flex items-center gap-1 text-sm font-semibold text-foreground-900">
                        <i className="ri-time-line text-accent-600"></i>
                        {item.startTime}
                        {item.endTime ? ` ~ ${item.endTime}` : ''}
                      </p>
                    )}

                    {item.kind === 'cancel' && (
                      <p className="flex items-center gap-1 text-sm font-semibold text-foreground-700">
                        <i className="ri-close-circle-line text-foreground-400"></i>
                        이 날 수업은 휴강이에요.
                      </p>
                    )}

                    {item.kind === 'makeup' && (
                      <>
                        <p className="flex items-center gap-1 text-sm font-semibold text-foreground-900">
                          <i className="ri-bookmark-line text-secondary-700"></i>
                          {item.reason}
                        </p>
                        {item.scope && (
                          <div className="mt-1.5">
                            <p className="flex items-center gap-1 text-[11px] font-semibold text-foreground-500">
                              <i className="ri-book-open-line text-primary-600"></i>
                              보충 수업 범위
                            </p>
                            <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-foreground-600">
                              {item.scope}
                            </p>
                          </div>
                        )}
                        {item.nextHomework && (
                          <div className="mt-1.5">
                            <p className="flex items-center gap-1 text-[11px] font-semibold text-foreground-500">
                              <i className="ri-edit-box-line text-secondary-700"></i>
                              다음 숙제
                            </p>
                            <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-foreground-600">
                              {item.nextHomework}
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {item.note && (
                      <p className="mt-1.5 flex items-start gap-1 text-xs text-foreground-600">
                        <i className="ri-sticky-note-line mt-0.5 text-foreground-400"></i>
                        {item.note}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}